import { useCallback, useMemo, useRef } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Flex } from "@similarweb/ui-components/foundations";

import { MarkdownMessage } from "app/pages/sales-intelligence/modules/ai-agent/components/MarkdownMessage/MarkdownMessage";
import type { MarkdownMessageProps } from "app/pages/sales-intelligence/modules/ai-agent/components/MarkdownMessage/types";
import { ChatWidget } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/ChatWidget/ChatWidget";
import { LinkWidget } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/LinkWidget/LinkWidget";
import { parseContentWithWidgets } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/utils/contentWithWidgetsParser";
import { getLinkWidgetsFromContent } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/utils/linkWidget";
import { getSuperAgentMarkdownComponents } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/utils/markdownComponents";
import type {
    ContactSearchEntry,
    CRMEntity,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

type Props = Omit<MarkdownMessageProps, "componentProps"> & {
    componentProps: {
        onExportToCRM: (contact: ContactSearchEntry, destination: Integration, as?: CRMEntity) => void;
    };
};

export const SuperAgentMessage = ({ content, componentProps, isLoading, messageId }: Props) => {
    const { translate } = useTranslate();

    const messageContentRef = useRef(content);
    const lastContentPropRef = useRef(content);
    // Keep the ref in sync with the latest `content` prop synchronously during render so that
    // child effects (e.g. ContactWidget firing immediately on a React Query cache hit) read the
    // current message, not a stale value from a previous parent render. Effects run children
    // first, so doing this in a parent useEffect would arrive too late. We only overwrite when
    // the prop actually changes, preserving augmented values written via setMessageContent.
    if (lastContentPropRef.current !== content) {
        lastContentPropRef.current = content;
        messageContentRef.current = content;
    }

    const getMessageContent = useCallback(() => messageContentRef.current, []);
    const setMessageContent = useCallback((updated: string) => {
        messageContentRef.current = updated;
    }, []);

    const segments = useMemo(() => parseContentWithWidgets(content), [content]);

    const markdownComponents = useMemo(() => getSuperAgentMarkdownComponents(componentProps), [componentProps]);

    const linkWidgets = useMemo(() => {
        if (isLoading) {
            return [];
        }
        return getLinkWidgetsFromContent(content);
    }, [isLoading, content]);

    return (
        <div>
            {segments.map((segment, index) =>
                segment.type === "markdown" ? (
                    <MarkdownMessage
                        key={`md-${index}`}
                        content={segment.content}
                        components={markdownComponents}
                        componentProps={componentProps}
                    />
                ) : (
                    <ChatWidget
                        key={`widget-${index}`}
                        config={segment.config}
                        onExportToCRM={componentProps.onExportToCRM}
                        getMessageContent={getMessageContent}
                        setMessageContent={setMessageContent}
                        messageId={messageId ?? ""}
                        isStreaming={!!isLoading}
                    />
                ),
            )}
            <Flex
                gap="16px"
                flexWrap="wrap"
            >
                {linkWidgets.map((widget) => (
                    <LinkWidget
                        key={widget.url}
                        title={translate(widget.title)}
                        subTitle={translate(widget.subTitle)}
                        url={widget.url}
                        iconName={widget.iconName}
                    />
                ))}
            </Flex>
        </div>
    );
};
